// Every caller of the openInNewWindow port reaches it from an HTTP response, because the
// file URL has to be fetched before it can be opened. No user gesture is live by then, so
// Safari (mobile Safari especially) blocks the pop-up and returns null, and the member sees
// nothing happen. Fall back to the current tab, which is never pop-up blocked.
//
// Do not pass "noopener": it makes window.open return null even when it succeeds, which
// would send us down the fallback on top of a tab that already opened.
function openUrl(url, win) {
  const newPage = win.open(url, "_blank");
  if (!newPage) {
    win.location.assign(url);
  }
}

// The portal's per-practice CSP is keyed on the viewer host, which the generic host cannot supply:
// member.hint.com names no practice, so the edge has nothing to look up and the document gets the
// baseline policy. Shared.elm already resolves the practice from ?subdomain=, so putting it in the
// URL is what lets the edge resolve it too.
//
// It has to be in the URL rather than supplied later, because a CSP is fixed when the document is
// fetched: this only pays off on the *next* document load -- a refresh, a shared link, a cold open
// of a URL we pushed -- which is exactly where the practice would otherwise be lost.
//
// Left alone when the host's first label is already the practice, so a practice subdomain keeps the
// clean URLs it has today.
const PRACTICE_PARAM = "subdomain";
const PRACTICE_PARAMS = [PRACTICE_PARAM, "practice"];
const PRACTICE_STORAGE_KEY = "accountPracticeId";

// localStorage throws rather than returning null in a private window or with site data blocked, and
// navigation must not depend on it.
function storedPractice(win) {
  try {
    return win.localStorage.getItem(PRACTICE_STORAGE_KEY);
  } catch {
    return null;
  }
}

function withPractice(url, win) {
  try {
    const resolved = new URL(url, win.location.href);
    const practice = storedPractice(win);

    if (!practice) return url;
    if (PRACTICE_PARAMS.some((name) => resolved.searchParams.has(name))) return url;
    if (resolved.hostname.split(".")[0] === practice) return url;

    resolved.searchParams.set(PRACTICE_PARAM, practice);

    // Kept in the shape it arrived in: the ports push a path, link hrefs are absolute.
    return /^[a-z][a-z0-9+.-]*:|^\/\//i.test(url) ? resolved.href : resolved.pathname + resolved.search + resolved.hash;
  } catch {
    return url;
  }
}

const NavigationPort = {
  openUrl: openUrl,
  withPractice: withPractice,

  add: function (app) {
    // Inform app of browser navigation (the BACK and FORWARD buttons)
    window.addEventListener("popstate", function () {
      app.ports.onUrlChange.send(location.href);
    });

    // Change the URL upon request, inform app of the change.
    app.ports.pushUrl.subscribe(function (url) {
      history.pushState({}, "", withPractice(url, window));
      app.ports.onUrlChange.send(location.href);
    });

    // Intercept link clicks to change the location without refreshing the page
    document.addEventListener("click", function (event) {
      const { target } = event;
      if (event.ctrlKey || event.metaKey) {
        return;
      }
      const isLocalHref = (targetA) => {
        return targetA.host == location.host;
      };
      const linkElement = target.localName == "a" ? target : target.closest("a");
      if (
        linkElement &&
        linkElement.href &&
        isLocalHref(linkElement) &&
        linkElement.target !== "_blank" &&
        linkElement.target !== "_self"
      ) {
        event.preventDefault();
        const href = withPractice(linkElement.href, window);
        history.pushState({}, "", href);
        app.ports.onUrlChange.send(href);
      }
    });

    if (app.ports.openInNewWindow) {
      app.ports.openInNewWindow.subscribe((url) => {
        openUrl(url, window);
      });
    }

    if (app.ports.replaceUrl) {
      app.ports.replaceUrl.subscribe((url) => {
        history.replaceState({}, "", withPractice(url, window));
      });
    }

    // A redirect: go there without leaving an entry that Back would redirect from again.
    if (app.ports.redirectUrl) {
      app.ports.redirectUrl.subscribe((url) => {
        history.replaceState({}, "", withPractice(url, window));
        app.ports.onUrlChange.send(location.href);
      });
    }
  },
};

module.exports = NavigationPort;
